import { useEffect, useRef, useState } from 'react';
import { Users, CreditCard, LayoutGrid, Pencil, Trash2, Plus, Save, X, Tags, MessageSquare, Sliders, FileText, Radio, RefreshCw, Eye, Mail, Send, Lock, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Navigate } from 'react-router-dom';

const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-GB') : '-';
const toInputDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ''; }
};
const toInputDateTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toISOString().slice(0, 16); } catch { return ''; }
};

const PAGE_SIZE = 50;

function TablePagination({ page, pageCount, onPage, total, testIdPrefix }) {
  if (pageCount <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border text-xs text-muted-foreground" data-testid={`${testIdPrefix}-pagination`}>
      <span>Viser side {page + 1} af {pageCount} · {total} i alt</span>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={page === 0} onClick={() => onPage(page - 1)} data-testid={`${testIdPrefix}-prev`}>Forrige</Button>
        <Button size="sm" variant="outline" disabled={page >= pageCount - 1} onClick={() => onPage(page + 1)} data-testid={`${testIdPrefix}-next`}>Næste</Button>
      </div>
    </div>
  );
}

function SearchBar({ value, onChange, placeholder, testId }) {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || 'Search…'}
      className="bg-[#09090B] border-border max-w-sm"
      data-testid={testId}
    />
  );
}

/* Shared user-form tabs used by both Edit and Add user dialogs */
function UserFormTabs({ data, setData, allCats, passwordLabel, passwordRequired, testPrefix }) {
  const set = (k, v) => setData({ ...data, [k]: v });
  const toggleCat = (id) => {
    const cur = data.categories || [];
    set('categories', cur.includes(id) ? cur.filter((c) => c !== id) : [...cur, id]);
  };
  const LabelCol = 'text-left text-xs font-medium text-muted-foreground';
  const InputCls = 'w-full bg-[#09090B] border-border';

  const [pwdVisible, setPwdVisible] = useState(false);
  const timerRef = useRef(null);
  const peekPwd = () => {
    setPwdVisible(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setPwdVisible(false), 2000);
  };
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <Tabs defaultValue="stam">
      <TabsList className="bg-[#09090B] border border-border">
        <TabsTrigger value="stam" data-testid={`${testPrefix}-tab-stam`}>Stamdata</TabsTrigger>
        <TabsTrigger value="prefs" data-testid={`${testPrefix}-tab-prefs`}>Præferencer</TabsTrigger>
        <TabsTrigger value="cats" data-testid={`${testPrefix}-tab-cats`}>Kategorier</TabsTrigger>
      </TabsList>

      <TabsContent value="stam" className="mt-4">
        <div className="grid grid-cols-[130px_1fr] items-center gap-x-4 gap-y-3">
          <Label className={LabelCol}>Nickname{passwordRequired ? ' *' : ''}</Label>
          {passwordRequired ? (
            <Input value={data.nickname || ''} onChange={(e) => set('nickname', e.target.value)} className={InputCls} required placeholder="e.g. Anna_K" data-testid={`${testPrefix}-nickname-input`} />
          ) : (
            <Input value={data.nickname || ''} readOnly disabled className={`${InputCls} text-muted-foreground cursor-not-allowed`} data-testid={`${testPrefix}-nickname-input`} />
          )}

          <Label className={LabelCol}>Email{passwordRequired ? ' *' : ''}</Label>
          <Input type="email" value={data.email || ''} onChange={(e) => set('email', e.target.value)} className={InputCls} data-testid={`${testPrefix}-email-input`} />

          <Label className={LabelCol}>Confirm email</Label>
          <Input
            type="email"
            value={data.email_confirm || ''}
            onChange={(e) => set('email_confirm', e.target.value)}
            className={InputCls}
            placeholder="Skriv email igen"
            data-testid={`${testPrefix}-email-confirm-input`}
          />

          <Label className={LabelCol}>{passwordLabel}</Label>
          <div className="relative">
            <Input
              type={pwdVisible ? 'text' : 'password'}
              value={data.password || ''}
              onChange={(e) => set('password', e.target.value)}
              className={`${InputCls} pr-10`}
              minLength={6}
              data-testid={`${testPrefix}-password-input`}
            />
            <button
              type="button"
              onClick={peekPwd}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-volt transition-colors"
              title="Vis i 2 sekunder"
              data-testid={`${testPrefix}-password-peek-btn`}
            >
              <Eye className={`w-4 h-4 ${pwdVisible ? 'text-volt' : ''}`} />
            </button>
          </div>

          <Label className={LabelCol}>Confirm password</Label>
          <Input
            type="password"
            value={data.password_confirm || ''}
            onChange={(e) => set('password_confirm', e.target.value)}
            className={InputCls}
            minLength={6}
            placeholder="Skriv adgangskode igen"
            data-testid={`${testPrefix}-password-confirm-input`}
          />
        </div>
      </TabsContent>

      <TabsContent value="prefs" className="mt-4">
        <div className="grid grid-cols-[130px_1fr] items-center gap-x-4 gap-y-3">
          <Label className={LabelCol}>Show video</Label>
          <Select value={data.show_video || 'ask'} onValueChange={(v) => set('show_video', v)}>
            <SelectTrigger className={InputCls} data-testid={`${testPrefix}-show-video-select`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#18181B] border-border">
              <SelectItem value="always">Always</SelectItem>
              <SelectItem value="never">Never</SelectItem>
              <SelectItem value="ask">Ask every time</SelectItem>
            </SelectContent>
          </Select>

          <Label className={LabelCol}>Show nickname</Label>
          <Select value={data.show_nickname || 'ask'} onValueChange={(v) => set('show_nickname', v)}>
            <SelectTrigger className={InputCls} data-testid={`${testPrefix}-show-nickname-select`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#18181B] border-border">
              <SelectItem value="always">Always</SelectItem>
              <SelectItem value="never">Never</SelectItem>
              <SelectItem value="ask">Ask every time</SelectItem>
            </SelectContent>
          </Select>

          <Label className={LabelCol}>Supervisor</Label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!data.this_is_supervisor}
              onChange={(e) => set('this_is_supervisor', e.target.checked)}
              className="w-4 h-4 accent-volt"
              data-testid={`${testPrefix}-this-is-supervisor-input`}
            />
            <span className="text-sm text-muted-foreground">Kan monitorere gratis-gruppen (viser deltager-tæller, ingen cap)</span>
          </label>
        </div>
      </TabsContent>

      <TabsContent value="cats" className="mt-4">
        <div className="grid grid-cols-[130px_1fr] items-start gap-x-4 gap-y-3">
          <Label className={`${LabelCol} pt-2`}>Categories</Label>
          {allCats.length === 0 ? (
            <p className="text-sm text-muted-foreground">No categories yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-categories-list`}>
              {allCats.map((c) => {
                const selected = (data.categories || []).includes(c.id);
                return (
                  <button key={c.id} type="button"
                    onClick={() => toggleCat(c.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${selected ? 'bg-volt text-black border-volt' : 'bg-[#09090B] text-muted-foreground border-border hover:border-volt/40 hover:text-white'}`}
                    data-testid={`${testPrefix}-cat-${c.id}`}>
                    {c.category || c.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;
  return (
    <div className="px-6 md:px-12 py-10 md:py-16 pb-24 md:pb-16 max-w-7xl">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Administrator</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Admin Panel</h1>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-[#18181B] border border-border flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-2" /> Users</TabsTrigger>
          <TabsTrigger value="subs" data-testid="admin-tab-subs"><CreditCard className="w-4 h-4 mr-2" /> Subscriptions</TabsTrigger>
          <TabsTrigger value="groups" data-testid="admin-tab-groups"><LayoutGrid className="w-4 h-4 mr-2" /> Groups</TabsTrigger>
          <TabsTrigger value="cats" data-testid="admin-tab-cats"><Tags className="w-4 h-4 mr-2" /> Categories</TabsTrigger>
          <TabsTrigger value="convs" data-testid="admin-tab-convs"><MessageSquare className="w-4 h-4 mr-2" /> Conversations</TabsTrigger>
          <TabsTrigger value="params" data-testid="admin-tab-params"><Sliders className="w-4 h-4 mr-2" /> Parameters</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="admin-tab-invoices"><FileText className="w-4 h-4 mr-2" /> Invoices</TabsTrigger>
          <TabsTrigger value="ice" data-testid="admin-tab-ice"><Radio className="w-4 h-4 mr-2" /> ICE</TabsTrigger>
          <TabsTrigger value="broadcast" data-testid="admin-tab-broadcast"><Mail className="w-4 h-4 mr-2" /> Broadcast</TabsTrigger>
          <TabsTrigger value="uspg" data-testid="admin-tab-uspg"><Lock className="w-4 h-4 mr-2" /> USERSPRIVATEGROUPS</TabsTrigger>
          <TabsTrigger value="fui" data-testid="admin-tab-fui"><Gift className="w-4 h-4 mr-2" /> FreeUsersInfo</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
        <TabsContent value="subs" className="mt-6"><SubsTab /></TabsContent>
        <TabsContent value="groups" className="mt-6"><GroupsTab /></TabsContent>
        <TabsContent value="cats" className="mt-6"><CategoriesTab /></TabsContent>
        <TabsContent value="convs" className="mt-6"><ConversationsTab /></TabsContent>
        <TabsContent value="params" className="mt-6"><ParametersTab /></TabsContent>
        <TabsContent value="invoices" className="mt-6"><InvoicesTab /></TabsContent>
        <TabsContent value="ice" className="mt-6"><IceStatusTab /></TabsContent>
        <TabsContent value="broadcast" className="mt-6"><BroadcastTab /></TabsContent>
        <TabsContent value="uspg" className="mt-6"><UsersPrivateGroupsTab /></TabsContent>
        <TabsContent value="fui" className="mt-6"><FreeUsersInfoTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- USERS ---------------- */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const emptyNewUser = { email: '', email_confirm: '', password: '', password_confirm: '', nickname: '', show_video: 'ask', show_nickname: 'ask', categories: [], this_is_supervisor: false };
  const [newUser, setNewUser] = useState(emptyNewUser);
  const [allCats, setAllCats] = useState([]);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([api.get('/admin/users'), api.get('/admin/categories').catch(() => ({ data: [] }))]);
      setUsers(u.data);
      setAllCats(c.data);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...editing };
      const origEmail = (payload._original_email || '').toLowerCase();
      const newEmail = (payload.email || '').toLowerCase();
      // Require email confirmation when email is being changed
      if (newEmail !== origEmail) {
        if ((payload.email_confirm || '').toLowerCase() !== newEmail) {
          toast.error('E-mail-adresserne stemmer ikke overens. Bekræft venligst.');
          return;
        }
      }
      // Nickname is immutable — always strip it out before sending
      delete payload.nickname;
      // Require password confirmation when password is being set
      if (payload.password) {
        if ((payload.password_confirm || '') !== payload.password) {
          toast.error('Adgangskoderne stemmer ikke overens.');
          return;
        }
      }
      delete payload.id;
      delete payload.email_confirm;
      delete payload.password_confirm;
      delete payload._original_email;
      if (!payload.password) delete payload.password;
      await api.put(`/admin/users/${editing.id}`, payload);
      toast.success('User updated');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const create = async () => {
    if (!newUser.email || !newUser.password) {
      toast.error('Email and password are required');
      return;
    }
    if (!(newUser.nickname || '').trim()) {
      toast.error('Nickname is required');
      return;
    }
    if (newUser.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if ((newUser.email_confirm || '').toLowerCase() !== newUser.email.toLowerCase()) {
      toast.error('E-mail-adresserne stemmer ikke overens.');
      return;
    }
    if ((newUser.password_confirm || '') !== newUser.password) {
      toast.error('Adgangskoderne stemmer ikke overens.');
      return;
    }
    try {
      const payload = { ...newUser };
      delete payload.email_confirm;
      delete payload.password_confirm;
      await api.post('/admin/users', payload);
      toast.success('User created');
      setCreating(false);
      setNewUser(emptyNewUser);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success('User deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SearchBar value={query} onChange={(v) => { setQuery(v); setPage(0); }} placeholder="Search nickname or email…" testId="users-search" />
        <Button
          onClick={() => setCreating(true)}
          className="bg-volt text-black hover:bg-volt font-semibold"
          data-testid="new-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add User
        </Button>
      </div>

      {(() => {
        const q = query.trim().toLowerCase();
        const filtered = q ? users.filter((u) =>
          `${u.nickname || ''} ${u.email}`.toLowerCase().includes(q)
        ) : users;
        const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
        const cur = Math.min(page, pageCount - 1);
        const rows = filtered.slice(cur * PAGE_SIZE, (cur + 1) * PAGE_SIZE);
        return (
          <div className="rounded-lg border border-border bg-[#18181B] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Nickname</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Categories</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="admin-users-table">
                {loading ? (
                  <TableRow className="border-border"><TableCell colSpan={4} className="text-center text-muted-foreground">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow className="border-border"><TableCell colSpan={4} className="text-center text-muted-foreground">No users match your search.</TableCell></TableRow>
                ) : rows.map((u) => (
                  <TableRow key={u.id} className="border-border" data-testid={`admin-user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.nickname || '—'}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell className="text-muted-foreground">{(u.categories || []).length}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setEditing({ ...u, password: '', email_confirm: u.email || '', _original_email: u.email || '' })} data-testid={`edit-user-btn-${u.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      {u.email !== 'dannie@yokano.org' && (
                        <Button size="sm" variant="ghost" onClick={() => remove(u.id)} className="text-destructive" data-testid={`delete-user-btn-${u.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination page={cur} pageCount={pageCount} onPage={setPage} total={filtered.length} testIdPrefix="users" />
          </div>
        );
      })()}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#18181B] border-border max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          {editing && (
            <UserFormTabs
              data={editing}
              setData={setEditing}
              allCats={allCats}
              passwordLabel="New password (leave empty to keep)"
              passwordRequired={false}
              testPrefix="edit-user"
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="save-user-btn"><Save className="w-4 h-4 mr-2" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={creating} onOpenChange={(v) => { setCreating(v); if (!v) setNewUser(emptyNewUser); }}>
        <DialogContent className="bg-[#18181B] border-border max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <UserFormTabs
            data={newUser}
            setData={setNewUser}
            allCats={allCats}
            passwordLabel="Password * (min 6 chars)"
            passwordRequired={true}
            testPrefix="new-user"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreating(false); setNewUser(emptyNewUser); }}>Cancel</Button>
            <Button onClick={create} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="create-user-btn">
              <Plus className="w-4 h-4 mr-2" /> Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- SUBSCRIPTIONS ---------------- */
function SubsTab() {
  const [subs, setSubs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newSub, setNewSub] = useState({ user_id: '', payment_date: '', expire_date: '', remaining_minutes: '', remaining_paid_minutes: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([api.get('/admin/subscriptions'), api.get('/admin/users')]);
      setSubs(s.data);
      setUsers(u.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const userName = (id) => {
    const u = users.find((x) => x.id === id);
    return u ? (u.nickname || u.email) : id;
  };

  const save = async () => {
    try {
      await api.put(`/admin/subscriptions/${editing.id}`, {
        payment_date: editing.payment_date,
        expire_date: editing.expire_date,
        remaining_minutes: parseInt(editing.remaining_minutes) || 0,
        remaining_paid_minutes: parseInt(editing.remaining_paid_minutes) || 0,
      });
      toast.success('Subscription updated');
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const create = async () => {
    if (!newSub.user_id) { toast.error('Select user'); return; }
    try {
      const payload = { user_id: newSub.user_id };
      if (newSub.payment_date) payload.payment_date = new Date(newSub.payment_date).toISOString();
      if (newSub.expire_date) payload.expire_date = new Date(newSub.expire_date).toISOString();
      if (newSub.remaining_minutes !== '' && newSub.remaining_minutes !== undefined) payload.remaining_minutes = parseInt(newSub.remaining_minutes) || 0;
      if (newSub.remaining_paid_minutes !== '' && newSub.remaining_paid_minutes !== undefined) payload.remaining_paid_minutes = parseInt(newSub.remaining_paid_minutes) || 0;
      await api.post('/admin/subscriptions', payload);
      toast.success('Subscription created');
      setCreating(false);
      setNewSub({ user_id: '', payment_date: '', expire_date: '', remaining_minutes: '', remaining_paid_minutes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Create failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this subscription?')) return;
    try {
      await api.delete(`/admin/subscriptions/${id}`);
      toast.success('Deleted'); load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="new-subscription-btn">
          <Plus className="w-4 h-4 mr-2" /> New Subscription
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-[#18181B] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>User</TableHead>
              <TableHead>Payment Date</TableHead>
              <TableHead>Expire Date</TableHead>
              <TableHead>Remaining min.</TableHead>
              <TableHead>Remaining Paid Minutes</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="admin-subs-table">
            {loading ? (
              <TableRow className="border-border"><TableCell colSpan={7} className="text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : subs.length === 0 ? (
              <TableRow className="border-border"><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No subscriptions.</TableCell></TableRow>
            ) : subs.map((s) => {
              const active = new Date(s.expire_date) > new Date();
              return (
                <TableRow key={s.id} className="border-border">
                  <TableCell className="font-medium">{userName(s.user_id)}</TableCell>
                  <TableCell>{fmt(s.payment_date)}</TableCell>
                  <TableCell>{fmt(s.expire_date)}</TableCell>
                  <TableCell>{s.remaining_minutes ?? 0}</TableCell>
                  <TableCell data-testid={`sub-remaining-paid-${s.id}`}>{s.remaining_paid_minutes ?? 0}</TableCell>
                  <TableCell>{active ? <Badge className="bg-volt/10 text-volt border-volt/30 hover:bg-volt/10">Active</Badge> : <Badge variant="outline" className="border-border text-muted-foreground">Expired</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing({ ...s, payment_date: toInputDate(s.payment_date), expire_date: toInputDate(s.expire_date) })}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(s.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader><DialogTitle>Edit Subscription</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Payment Date</Label><Input type="date" value={editing.payment_date} onChange={(e) => setEditing({ ...editing, payment_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>Expire Date</Label><Input type="date" value={editing.expire_date} onChange={(e) => setEditing({ ...editing, expire_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>Remaining minutes</Label><Input type="number" min="0" value={editing.remaining_minutes ?? 0} onChange={(e) => setEditing({ ...editing, remaining_minutes: e.target.value })} className="bg-[#09090B] border-border mt-1.5" data-testid="edit-sub-remaining-input" /></div>
              <div><Label>Remaining Paid Minutes</Label><Input type="number" min="0" value={editing.remaining_paid_minutes ?? 0} onChange={(e) => setEditing({ ...editing, remaining_paid_minutes: e.target.value })} className="bg-[#09090B] border-border mt-1.5" data-testid="edit-sub-remaining-paid-input" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => {
              const payload = { ...editing };
              if (payload.payment_date) payload.payment_date = new Date(payload.payment_date).toISOString();
              if (payload.expire_date) payload.expire_date = new Date(payload.expire_date).toISOString();
              setEditing(payload);
              save();
            }} className="bg-volt text-black hover:bg-volt font-semibold"><Save className="w-4 h-4 mr-2" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader><DialogTitle>Create Subscription</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>User</Label>
              <Select value={newSub.user_id} onValueChange={(v) => setNewSub({ ...newSub, user_id: v })}>
                <SelectTrigger className="bg-[#09090B] border-border mt-1.5" data-testid="new-sub-user-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent className="bg-[#18181B] border-border">
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.nickname || u.email} ({u.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Payment Date (optional)</Label><Input type="date" value={newSub.payment_date} onChange={(e) => setNewSub({ ...newSub, payment_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            <div><Label>Expire Date (optional, defaults to +90 days)</Label><Input type="date" value={newSub.expire_date} onChange={(e) => setNewSub({ ...newSub, expire_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            <div><Label>Remaining minutes</Label><Input type="number" min="0" value={newSub.remaining_minutes} onChange={(e) => setNewSub({ ...newSub, remaining_minutes: e.target.value })} className="bg-[#09090B] border-border mt-1.5" placeholder="0" data-testid="new-sub-remaining-input" /></div>
            <div><Label>Remaining Paid Minutes</Label><Input type="number" min="0" value={newSub.remaining_paid_minutes} onChange={(e) => setNewSub({ ...newSub, remaining_paid_minutes: e.target.value })} className="bg-[#09090B] border-border mt-1.5" placeholder="0" data-testid="new-sub-remaining-paid-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={create} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="create-subscription-btn"><Plus className="w-4 h-4 mr-2" /> Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- GROUPS ---------------- */
function GroupsTab() {
  const [groups, setGroups] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', category_id: '', active_group: true, audio_only: false, private: false });
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [g, c] = await Promise.all([
        api.get('/admin/groups'),
        api.get('/admin/categories').catch(() => ({ data: [] })),
      ]);
      setGroups(g.data);
      setCats(c.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put(`/admin/groups/${editing.id}`, {
        name: editing.name,
        active_group: editing.active_group !== false,
        active_users: parseInt(editing.active_users) || 0,
      });
      toast.success('Group updated');
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const create = async () => {
    if (!newGroup.name) { toast.error('Name required'); return; }
    if (!newGroup.category_id) { toast.error('Category required'); return; }
    try {
      await api.post('/admin/groups', newGroup);
      toast.success('Group created');
      setCreating(false);
      setNewGroup({ name: '', category_id: '', active_group: true, audio_only: false, private: false });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Create failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this group?')) return;
    try {
      await api.delete(`/admin/groups/${id}`);
      toast.success('Deleted'); load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SearchBar value={query} onChange={(v) => { setQuery(v); setPage(0); }} placeholder="Search group name…" testId="groups-search" />
        <Button onClick={() => setCreating(true)} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="new-group-btn">
          <Plus className="w-4 h-4 mr-2" /> New Group
        </Button>
      </div>

      {(() => {
        const q = query.trim().toLowerCase();
        const filtered = q ? groups.filter((g) => (g.name || '').toLowerCase().includes(q)) : groups;
        const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
        const cur = Math.min(page, pageCount - 1);
        const rows = filtered.slice(cur * PAGE_SIZE, (cur + 1) * PAGE_SIZE);
        return (
          <div className="rounded-lg border border-border bg-[#18181B] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Category ID</TableHead>
                  <TableHead>Active Users</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead>Audio Only</TableHead>
                  <TableHead>Private</TableHead>
                  <TableHead>Auto-spawned</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="admin-groups-table">
                {loading ? (
                  <TableRow className="border-border"><TableCell colSpan={10} className="text-center text-muted-foreground">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow className="border-border"><TableCell colSpan={10} className="text-center text-muted-foreground">No groups match.</TableCell></TableRow>
                ) : rows.map((g) => (
                  <TableRow key={g.id} className="border-border">
                  <TableCell className="font-mono text-[11px] text-muted-foreground select-all break-all" title={g.id}>{g.id}</TableCell>
                    <TableCell className="font-medium">{g.name}</TableCell>
                    <TableCell className="font-mono text-[10px] text-muted-foreground max-w-[100px] truncate" title={g.category_id}>{g.category_id ? `${g.category_id.slice(0, 8)}…` : '—'}</TableCell>
                    <TableCell>{g.active_users}</TableCell>
                    <TableCell>{g.active_group !== false ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{g.audio_only ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{g.private ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{g.auto_spawned ? 'Yes' : '—'}</TableCell>
                    <TableCell className="text-muted-foreground">{fmt(g.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setEditing({ ...g })}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(g.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination page={cur} pageCount={pageCount} onPage={setPage} total={filtered.length} testIdPrefix="groups" />
          </div>
        );
      })()}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader><DialogTitle>Edit Group</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>Active Users</Label><Input type="number" value={editing.active_users} onChange={(e) => setEditing({ ...editing, active_users: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div className="flex items-center justify-between rounded-md border border-border p-3"><Label>Active group</Label><Switch checked={editing.active_group !== false} onCheckedChange={(v) => setEditing({ ...editing, active_group: v })} data-testid="edit-group-active-switch" /></div>
              <div className="flex items-center justify-between rounded-md border border-border p-3"><Label>Audio only (no video)</Label><Switch checked={!!editing.audio_only} onCheckedChange={(v) => setEditing({ ...editing, audio_only: v })} data-testid="edit-group-audio-only-switch" /></div>
              <div className="flex items-center justify-between rounded-md border border-border p-3"><Label>Private group</Label><Switch checked={!!editing.private} onCheckedChange={(v) => setEditing({ ...editing, private: v })} data-testid="edit-group-private-switch" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-volt text-black hover:bg-volt font-semibold"><Save className="w-4 h-4 mr-2" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader><DialogTitle>Create Group</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={newGroup.name} onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })} className="bg-[#09090B] border-border mt-1.5" data-testid="new-group-name-input" /></div>
            <div>
              <Label>Category *</Label>
              <Select value={newGroup.category_id} onValueChange={(v) => setNewGroup({ ...newGroup, category_id: v })}>
                <SelectTrigger className="bg-[#09090B] border-border mt-1.5" data-testid="new-group-category-select"><SelectValue placeholder="Pick category…" /></SelectTrigger>
                <SelectContent className="bg-[#18181B] border-border">
                  {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.category || c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3"><Label>Audio only (no video)</Label><Switch checked={newGroup.audio_only} onCheckedChange={(v) => setNewGroup({ ...newGroup, audio_only: v })} data-testid="new-group-audio-only-switch" /></div>
            <div className="flex items-center justify-between rounded-md border border-border p-3"><Label>Private group</Label><Switch checked={newGroup.private} onCheckedChange={(v) => setNewGroup({ ...newGroup, private: v })} data-testid="new-group-private-switch" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={create} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="create-group-btn"><Plus className="w-4 h-4 mr-2" /> Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ---------------- CATEGORIES ---------------- */
function CategoriesTab() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newCat, setNewCat] = useState({ category: '', audio_only: false, private: false, expired_date: '' });
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  const load = async () => {
    setLoading(true);
    try { setCats((await api.get('/admin/categories')).data); }
    catch { toast.error('Failed to load categories'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newCat.category.trim()) { toast.error('Category required'); return; }
    try {
      await api.post('/admin/categories', {
        ...newCat,
        expired_date: newCat.expired_date ? new Date(newCat.expired_date).toISOString() : null,
      });
      toast.success('Category created (and matching group)'); setCreating(false); setNewCat({ category: '', audio_only: false, private: false, expired_date: '' }); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Create failed'); }
  };

  const save = async () => {
    try {
      await api.put(`/admin/categories/${editing.id}`, {
        category: editing.category,
        audio_only: !!editing.audio_only,
        private: !!editing.private,
        expired_date: editing.expired_date ? new Date(editing.expired_date).toISOString() : null,
      });
      toast.success('Category updated'); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this category? Matching groups will be deactivated.')) return;
    try { await api.delete(`/admin/categories/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SearchBar value={query} onChange={(v) => { setQuery(v); setPage(0); }} placeholder="Search category…" testId="cats-search" />
        <Button onClick={() => setCreating(true)} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="new-category-btn">
          <Plus className="w-4 h-4 mr-2" /> New Category
        </Button>
      </div>
      {(() => {
        const q = query.trim().toLowerCase();
        const filtered = q ? cats.filter((c) => (c.category || '').toLowerCase().includes(q)) : cats;
        const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
        const cur = Math.min(page, pageCount - 1);
        const rows = filtered.slice(cur * PAGE_SIZE, (cur + 1) * PAGE_SIZE);
        return (
          <div className="rounded-lg border border-border bg-[#18181B] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>ID</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Audio Only</TableHead>
                  <TableHead>Private</TableHead>
                  <TableHead>Expired Date</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="admin-categories-table">
                {loading ? (
                  <TableRow className="border-border"><TableCell colSpan={7} className="text-center text-muted-foreground">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow className="border-border"><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No categories match.</TableCell></TableRow>
                ) : rows.map((c) => (
                  <TableRow key={c.id} className="border-border">
                    <TableCell className="font-mono text-[10px] text-muted-foreground max-w-[100px] truncate" title={c.id}>{c.id?.slice(0, 8)}…</TableCell>
                    <TableCell className="font-medium">{c.category}</TableCell>
                    <TableCell>{c.audio_only ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{c.private ? 'Yes' : 'No'}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{c.expired_date ? fmt(c.expired_date) : '—'}</TableCell>
                    <TableCell className="text-muted-foreground">{fmt(c.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setEditing({ ...c })}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(c.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination page={cur} pageCount={pageCount} onPage={setPage} total={filtered.length} testIdPrefix="cats" />
          </div>
        );
      })()}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader><DialogTitle>Create Category</DialogTitle><DialogDescription>A matching Group will be auto-created.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Category</Label><Input value={newCat.category} onChange={(e) => setNewCat({ ...newCat, category: e.target.value })} className="bg-[#09090B] border-border mt-1.5" data-testid="new-category-name-input" /></div>
            <div className="flex items-center justify-between rounded-md border border-border p-3"><Label>Audio only</Label><Switch checked={newCat.audio_only} onCheckedChange={(v) => setNewCat({ ...newCat, audio_only: v })} data-testid="new-category-audio-only-switch" /></div>
            <div className="flex items-center justify-between rounded-md border border-border p-3"><Label>Private group</Label><Switch checked={newCat.private} onCheckedChange={(v) => setNewCat({ ...newCat, private: v })} data-testid="new-category-private-switch" /></div>
            <div><Label>Expired date (optional)</Label><Input type="datetime-local" value={newCat.expired_date} onChange={(e) => setNewCat({ ...newCat, expired_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" data-testid="new-category-expired-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={create} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="create-category-btn"><Plus className="w-4 h-4 mr-2" /> Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader><DialogTitle>Edit Category</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Category</Label><Input value={editing.category || ''} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div className="flex items-center justify-between rounded-md border border-border p-3"><Label>Audio only</Label><Switch checked={!!editing.audio_only} onCheckedChange={(v) => setEditing({ ...editing, audio_only: v })} /></div>
              <div className="flex items-center justify-between rounded-md border border-border p-3"><Label>Private group</Label><Switch checked={!!editing.private} onCheckedChange={(v) => setEditing({ ...editing, private: v })} /></div>
              <div><Label>Expired date (optional)</Label><Input type="datetime-local" value={(editing.expired_date || '').slice(0, 16)} onChange={(e) => setEditing({ ...editing, expired_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" data-testid="edit-category-expired-input" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-volt text-black hover:bg-volt font-semibold"><Save className="w-4 h-4 mr-2" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- CONVERSATIONS ---------------- */
function ConversationsTab() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newRow, setNewRow] = useState({ user_id: '', group_id: '', joined_at: '', left_at: '', ip_address: '', device_name: '' });
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [c, u, g] = await Promise.all([api.get('/admin/conversations'), api.get('/admin/users'), api.get('/admin/groups')]);
      setRows(c.data); setUsers(u.data); setGroups(g.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const userName = (id) => { const u = users.find((x) => x.id === id); return u ? (u.nickname || u.email) : id?.slice(0, 8); };
  const groupName = (id) => groups.find((g) => g.id === id)?.name || id?.slice(0, 8);

  const create = async () => {
    if (!newRow.user_id || !newRow.group_id) { toast.error('Select user and group'); return; }
    try {
      const payload = { user_id: newRow.user_id, group_id: newRow.group_id };
      if (newRow.joined_at) payload.joined_at = new Date(newRow.joined_at).toISOString();
      if (newRow.left_at) payload.left_at = new Date(newRow.left_at).toISOString();
      if (newRow.ip_address) payload.ip_address = newRow.ip_address;
      if (newRow.device_name) payload.device_name = newRow.device_name;
      await api.post('/admin/conversations', payload);
      toast.success('Conversation created'); setCreating(false); setNewRow({ user_id: '', group_id: '', joined_at: '', left_at: '', ip_address: '', device_name: '' }); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Create failed'); }
  };

  const save = async () => {
    try {
      const payload = {};
      if (editing.joined_at) payload.joined_at = new Date(editing.joined_at).toISOString();
      payload.left_at = editing.left_at ? new Date(editing.left_at).toISOString() : null;
      payload.ip_address = editing.ip_address || '';
      payload.device_name = editing.device_name || '';
      await api.put(`/admin/conversations/${editing.id}`, payload);
      toast.success('Updated'); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this conversation record?')) return;
    try { await api.delete(`/admin/conversations/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SearchBar value={query} onChange={(v) => { setQuery(v); setPage(0); }} placeholder="Search user or group…" testId="convs-search" />
        <Button onClick={() => setCreating(true)} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="new-conversation-btn">
          <Plus className="w-4 h-4 mr-2" /> New Conversation
        </Button>
      </div>
      {(() => {
        const q = query.trim().toLowerCase();
        const filtered = q ? rows.filter((r) => `${userName(r.user_id)} ${groupName(r.group_id)}`.toLowerCase().includes(q)) : rows;
        const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
        const cur = Math.min(page, pageCount - 1);
        const pageRows = filtered.slice(cur * PAGE_SIZE, (cur + 1) * PAGE_SIZE);
        return (
          <div className="rounded-lg border border-border bg-[#18181B] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>User</TableHead>
                  <TableHead>Group</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Left</TableHead>
                  <TableHead>Minutes</TableHead>
                  <TableHead>IP address</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="admin-conversations-table">
                {loading ? (
                  <TableRow className="border-border"><TableCell colSpan={8} className="text-center text-muted-foreground">Loading…</TableCell></TableRow>
                ) : pageRows.length === 0 ? (
                  <TableRow className="border-border"><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No conversations match.</TableCell></TableRow>
                ) : pageRows.map((r) => (
                  <TableRow key={r.id} className="border-border">
                    <TableCell className="font-medium">{userName(r.user_id)}</TableCell>
                    <TableCell>{groupName(r.group_id)}</TableCell>
                    <TableCell>{fmt(r.joined_at)}</TableCell>
                    <TableCell>{r.left_at ? fmt(r.left_at) : <Badge className="bg-volt/10 text-volt border-volt/30 hover:bg-volt/10">Active</Badge>}</TableCell>
                    <TableCell>{r.minutes}</TableCell>
                    <TableCell className="font-mono text-xs">{r.ip_address || '—'}</TableCell>
                    <TableCell className="text-xs max-w-[220px] truncate" title={r.device_name}>{r.device_name || '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r, joined_at: toInputDateTime(r.joined_at), left_at: toInputDateTime(r.left_at) })}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination page={cur} pageCount={pageCount} onPage={setPage} total={filtered.length} testIdPrefix="convs" />
          </div>
        );
      })()}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader><DialogTitle>Create Conversation Record</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>User</Label>
              <Select value={newRow.user_id} onValueChange={(v) => setNewRow({ ...newRow, user_id: v })}>
                <SelectTrigger className="bg-[#09090B] border-border mt-1.5" data-testid="new-conv-user-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent className="bg-[#18181B] border-border">{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.nickname || u.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Group</Label>
              <Select value={newRow.group_id} onValueChange={(v) => setNewRow({ ...newRow, group_id: v })}>
                <SelectTrigger className="bg-[#09090B] border-border mt-1.5" data-testid="new-conv-group-select"><SelectValue placeholder="Select group" /></SelectTrigger>
                <SelectContent className="bg-[#18181B] border-border">{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Joined at (optional)</Label><Input type="datetime-local" value={newRow.joined_at} onChange={(e) => setNewRow({ ...newRow, joined_at: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            <div><Label>Left at (optional)</Label><Input type="datetime-local" value={newRow.left_at} onChange={(e) => setNewRow({ ...newRow, left_at: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            <div><Label>IP address (optional)</Label><Input value={newRow.ip_address} onChange={(e) => setNewRow({ ...newRow, ip_address: e.target.value })} className="bg-[#09090B] border-border mt-1.5" placeholder="e.g. 192.168.1.42" data-testid="new-conv-ip-input" /></div>
            <div><Label>Device name (optional)</Label><Input value={newRow.device_name} onChange={(e) => setNewRow({ ...newRow, device_name: e.target.value })} className="bg-[#09090B] border-border mt-1.5" placeholder="e.g. iPhone 15 / Chrome" data-testid="new-conv-device-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={create} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="create-conversation-btn"><Plus className="w-4 h-4 mr-2" /> Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader><DialogTitle>Edit Conversation</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Joined at</Label><Input type="datetime-local" value={editing.joined_at} onChange={(e) => setEditing({ ...editing, joined_at: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>Left at (empty = still active)</Label><Input type="datetime-local" value={editing.left_at || ''} onChange={(e) => setEditing({ ...editing, left_at: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>IP address</Label><Input value={editing.ip_address || ''} onChange={(e) => setEditing({ ...editing, ip_address: e.target.value })} className="bg-[#09090B] border-border mt-1.5" data-testid="edit-conv-ip-input" /></div>
              <div><Label>Device name</Label><Input value={editing.device_name || ''} onChange={(e) => setEditing({ ...editing, device_name: e.target.value })} className="bg-[#09090B] border-border mt-1.5" data-testid="edit-conv-device-input" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-volt text-black hover:bg-volt font-semibold"><Save className="w-4 h-4 mr-2" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- PARAMETERS ---------------- */
function ParametersTab() {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setP((await api.get('/parameters')).data); }
    catch { toast.error('Failed to load parameters'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        next_invoice_number: parseInt(p.next_invoice_number) || 0,
        monthly_price_first: parseFloat(p.monthly_price_first) || 0,
        monthly_price_recurring: parseFloat(p.monthly_price_recurring) || 0,
        quarterly_price_first: parseFloat(p.quarterly_price_first) || 0,
        quarterly_price_recurring: parseFloat(p.quarterly_price_recurring) || 0,
        fair_use_audio_min: parseInt(p.fair_use_audio_min) || 0,
        fair_use_video_min: parseInt(p.fair_use_video_min) || 0,
        seconds_update_groups: parseInt(p.seconds_update_groups) || 30,
        users_per_group: parseInt(p.users_per_group) || 6,
        max_private_groups: parseInt(p.max_private_groups) || 10,
        free_minutes: parseInt(p.free_minutes) || 0,
        minutes_bought: parseInt(p.minutes_bought) || 0,
        price_paid_minutes_first_order: parseFloat(p.price_paid_minutes_first_order) || 0,
        price_paid_minutes_recurring_orders: parseFloat(p.price_paid_minutes_recurring_orders) || 0,
        company_name: p.company_name || '',
        vat_number: p.vat_number || '',
        clarity_enabled: !!p.clarity_enabled,
      };
      await api.put('/admin/parameters', payload);
      toast.success('Parameters saved');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading || !p) return <div className="text-center py-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-2xl space-y-6 rounded-lg border border-border bg-[#18181B] p-6 md:p-8">
      <div>
        <h3 className="font-display text-xl font-bold mb-1">Invoice Numbering</h3>
        <Label>Next Invoice Number</Label>
        <Input type="number" value={p.next_invoice_number} onChange={(e) => setP({ ...p, next_invoice_number: e.target.value })}
          className="bg-[#09090B] border-border mt-1.5" data-testid="param-next-invoice-input" />
        <p className="text-xs text-muted-foreground mt-1.5">Auto-incremented on every payment. Adjust with care.</p>
      </div>

      <div>
        <h3 className="font-display text-xl font-bold mb-3">Monthly Pricing (DKK, incl. VAT)</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>First-time price</Label>
            <Input type="number" step="0.01" value={p.monthly_price_first} onChange={(e) => setP({ ...p, monthly_price_first: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" data-testid="param-monthly-first-input" />
          </div>
          <div>
            <Label>Recurring price (2nd time onwards)</Label>
            <Input type="number" step="0.01" value={p.monthly_price_recurring} onChange={(e) => setP({ ...p, monthly_price_recurring: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" data-testid="param-monthly-recurring-input" />
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-display text-xl font-bold mb-3">Quarterly Pricing (DKK, incl. VAT)</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>First-time price</Label>
            <Input type="number" step="0.01" value={p.quarterly_price_first} onChange={(e) => setP({ ...p, quarterly_price_first: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" data-testid="param-quarterly-first-input" />
          </div>
          <div>
            <Label>Recurring price (2nd time onwards)</Label>
            <Input type="number" step="0.01" value={p.quarterly_price_recurring} onChange={(e) => setP({ ...p, quarterly_price_recurring: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" data-testid="param-quarterly-recurring-input" />
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-display text-xl font-bold mb-3">Fair Use (minutes / month)</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Audio</Label>
            <Input type="number" value={p.fair_use_audio_min} onChange={(e) => setP({ ...p, fair_use_audio_min: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" data-testid="param-fair-audio-input" />
          </div>
          <div>
            <Label>Video</Label>
            <Input type="number" value={p.fair_use_video_min} onChange={(e) => setP({ ...p, fair_use_video_min: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" data-testid="param-fair-video-input" />
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-display text-xl font-bold mb-3">Groups behaviour</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Seconds between dashboard updates</Label>
            <Input type="number" min="5" value={p.seconds_update_groups ?? 30} onChange={(e) => setP({ ...p, seconds_update_groups: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" data-testid="param-seconds-update-input" />
          </div>
          <div>
            <Label>Users per group (max)</Label>
            <Input type="number" min="2" max="20" value={p.users_per_group ?? 6} onChange={(e) => setP({ ...p, users_per_group: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" data-testid="param-users-per-group-input" />
          </div>
          <div className="col-span-2">
            <Label>Max number of private Groups</Label>
            <Input type="number" min="0" value={p.max_private_groups ?? 10} onChange={(e) => setP({ ...p, max_private_groups: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" data-testid="param-max-private-groups-input" />
            <p className="text-xs text-muted-foreground mt-1">Upper cap on how many private groups can exist at once.</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Dashboard auto-refreshes every N seconds. New groups auto-spawn when a group reaches the users-per-group cap and no other group in that category has room.</p>
      </div>

      <div>
        <h3 className="font-display text-xl font-bold mb-3">Free tier & Company</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Free Minutes</Label>
            <Input type="number" min="0" value={p.free_minutes ?? 60} onChange={(e) => setP({ ...p, free_minutes: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" data-testid="param-free-minutes-input" />
            <p className="text-xs text-muted-foreground mt-1">Default free minutes granted to new free-tier signups.</p>
          </div>
          <div />
          <div>
            <Label>Company Name</Label>
            <Input value={p.company_name || ''} onChange={(e) => setP({ ...p, company_name: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" placeholder="samtale24-7.dk" data-testid="param-company-name-input" />
          </div>
          <div>
            <Label>VAT Number</Label>
            <Input value={p.vat_number || ''} onChange={(e) => setP({ ...p, vat_number: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" placeholder="DK12345678" data-testid="param-vat-number-input" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Company name and VAT number are printed on every invoice PDF.</p>
      </div>

      <div>
        <h3 className="font-display text-xl font-bold mb-3">Paid Minutes (hourly package)</h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Minutes bought</Label>
            <Input type="number" min="0" value={p.minutes_bought ?? 60} onChange={(e) => setP({ ...p, minutes_bought: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" data-testid="param-minutes-bought-input" />
            <p className="text-xs text-muted-foreground mt-1">Minutes granted per hourly purchase.</p>
          </div>
          <div>
            <Label>Price paid minutes first order (DKK)</Label>
            <Input type="number" step="0.01" min="0" value={p.price_paid_minutes_first_order ?? 39} onChange={(e) => setP({ ...p, price_paid_minutes_first_order: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" data-testid="param-price-paid-minutes-first-input" />
          </div>
          <div>
            <Label>Price paid minutes recurring orders (DKK)</Label>
            <Input type="number" step="0.01" min="0" value={p.price_paid_minutes_recurring_orders ?? 49} onChange={(e) => setP({ ...p, price_paid_minutes_recurring_orders: e.target.value })}
              className="bg-[#09090B] border-border mt-1.5" data-testid="param-price-paid-minutes-recurring-input" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Used on the Payment page hourly card and on every hourly purchase.</p>
      </div>

      <div>
        <h3 className="font-display text-xl font-bold mb-3">Analytics</h3>
        <div className="flex items-center justify-between rounded-md border border-border p-3">
          <div>
            <Label>Clarity enabled</Label>
            <p className="text-xs text-muted-foreground mt-1">
              Turns Microsoft Clarity tracking on/off across the public homepage and the payment page.
              Requires <code className="text-white">REACT_APP_CLARITY_ID</code> in the frontend environment.
            </p>
          </div>
          <Switch
            checked={!!p.clarity_enabled}
            onCheckedChange={(v) => setP({ ...p, clarity_enabled: v })}
            data-testid="param-clarity-enabled-switch" />
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="save-params-btn">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save parameters'}
      </Button>
    </div>
  );
}

/* ---------------- INVOICES ---------------- */
function InvoicesTab() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const emptyNew = { user_id: '', amount_incl_vat: '', invoice_date: '', payment_date: '' };
  const [newRow, setNewRow] = useState(emptyNew);

  const load = async () => {
    setLoading(true);
    try {
      const [i, u] = await Promise.all([api.get('/admin/invoices'), api.get('/admin/users')]);
      setRows(i.data); setUsers(u.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const userName = (id) => { const u = users.find((x) => x.id === id); return u ? (u.nickname || u.email) : id?.slice(0, 8); };

  const create = async () => {
    if (!newRow.user_id || !newRow.amount_incl_vat) { toast.error('User and amount required'); return; }
    try {
      const payload = { user_id: newRow.user_id, amount_incl_vat: parseFloat(newRow.amount_incl_vat) };
      if (newRow.invoice_date) payload.invoice_date = new Date(newRow.invoice_date).toISOString();
      if (newRow.payment_date) payload.payment_date = new Date(newRow.payment_date).toISOString();
      await api.post('/admin/invoices', payload);
      toast.success('Invoice created'); setCreating(false); setNewRow(emptyNew); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Create failed'); }
  };

  const save = async () => {
    try {
      const payload = { amount_incl_vat: parseFloat(editing.amount_incl_vat) };
      if (editing.invoice_date) payload.invoice_date = new Date(editing.invoice_date).toISOString();
      if (editing.payment_date) payload.payment_date = new Date(editing.payment_date).toISOString();
      if (editing.user_name !== undefined) payload.user_name = editing.user_name;
      if (editing.user_email !== undefined) payload.user_email = editing.user_email;
      await api.put(`/admin/invoices/${editing.id}`, payload);
      toast.success('Updated'); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try { await api.delete(`/admin/invoices/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="new-invoice-btn">
          <Plus className="w-4 h-4 mr-2" /> New Invoice
        </Button>
      </div>
      <div className="rounded-lg border border-border bg-[#18181B] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Invoice #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Amount incl. VAT</TableHead>
              <TableHead>Payment date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="admin-invoices-table">
            {loading ? (
              <TableRow className="border-border"><TableCell colSpan={6} className="text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow className="border-border"><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No invoices yet.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} className="border-border">
                <TableCell className="font-mono">#{r.invoice_number}</TableCell>
                <TableCell>{fmt(r.invoice_date)}</TableCell>
                <TableCell>{r.user_name || userName(r.user_id)}</TableCell>
                <TableCell>{r.amount_incl_vat?.toFixed(2)} DKK</TableCell>
                <TableCell>{fmt(r.payment_date)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r, invoice_date: toInputDateTime(r.invoice_date), payment_date: toInputDateTime(r.payment_date) })}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader><DialogTitle>Create Invoice</DialogTitle><DialogDescription>Invoice number is auto-assigned from Parameters.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>User</Label>
              <Select value={newRow.user_id} onValueChange={(v) => setNewRow({ ...newRow, user_id: v })}>
                <SelectTrigger className="bg-[#09090B] border-border mt-1.5" data-testid="new-invoice-user-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent className="bg-[#18181B] border-border">{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.nickname || u.email} ({u.email})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Amount incl. VAT (DKK)</Label><Input type="number" step="0.01" value={newRow.amount_incl_vat} onChange={(e) => setNewRow({ ...newRow, amount_incl_vat: e.target.value })} className="bg-[#09090B] border-border mt-1.5" data-testid="new-invoice-amount-input" /></div>
            <div><Label>Invoice date (optional)</Label><Input type="datetime-local" value={newRow.invoice_date} onChange={(e) => setNewRow({ ...newRow, invoice_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            <div><Label>Payment date (optional)</Label><Input type="datetime-local" value={newRow.payment_date} onChange={(e) => setNewRow({ ...newRow, payment_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={create} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="create-invoice-btn"><Plus className="w-4 h-4 mr-2" /> Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader><DialogTitle>Edit Invoice #{editing?.invoice_number}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>User name (snapshot)</Label><Input value={editing.user_name || ''} onChange={(e) => setEditing({ ...editing, user_name: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>User email (snapshot)</Label><Input value={editing.user_email || ''} onChange={(e) => setEditing({ ...editing, user_email: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>Amount incl. VAT (DKK)</Label><Input type="number" step="0.01" value={editing.amount_incl_vat} onChange={(e) => setEditing({ ...editing, amount_incl_vat: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>Invoice date</Label><Input type="datetime-local" value={editing.invoice_date} onChange={(e) => setEditing({ ...editing, invoice_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>Payment date</Label><Input type="datetime-local" value={editing.payment_date} onChange={(e) => setEditing({ ...editing, payment_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-volt text-black hover:bg-volt font-semibold"><Save className="w-4 h-4 mr-2" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ---------------- ICE STATUS ---------------- */
function IceStatusTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/ice-status');
      setData(r.data);
    } catch { toast.error('Failed to load ICE status'); }
    finally { setLoading(false); }
  };

  const forceRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post('/admin/ice-status/refresh');
      toast.success(`Refreshed — source: ${r.data.source} (${r.data.server_count} servers)`);
      await load();
    } catch { toast.error('Refresh failed'); }
    finally { setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return <div className="text-muted-foreground text-sm">Indlæser ICE-status…</div>;
  }

  const src = data.last_source || '—';
  const status = data.last_status;
  const isMetered = src === 'metered';
  const statusColor = isMetered ? 'text-volt' : 'text-yellow-400';

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">ICE Server Status</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Live-diagnose af WebRTC-forbindelseslag (STUN + TURN).
          </p>
        </div>
        <Button
          onClick={forceRefresh}
          disabled={refreshing}
          className="bg-volt text-black hover:bg-volt font-semibold gap-2"
          data-testid="ice-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Force refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-[#18181B] p-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Aktuel kilde</div>
          <div className={`font-display text-2xl font-bold ${statusColor}`} data-testid="ice-source">
            {isMetered ? 'Metered.ca (dynamisk)' : 'Fallback (env)'}
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            Sidste fetch: {data.last_fetch_at ? new Date(data.last_fetch_at).toLocaleString('da-DK') : '—'}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-[#18181B] p-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Cache-alder</div>
          <div className="font-display text-2xl font-bold">
            {data.cache_age_seconds !== null && data.cache_age_seconds !== undefined
              ? `${data.cache_age_seconds}s`
              : '—'}
            <span className="text-sm text-muted-foreground font-normal ml-2">
              / {data.cache_ttl_seconds}s TTL
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            Udløber om {data.cache_ttl_remaining_seconds ?? 0}s
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-[#18181B] p-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Metered endpoint</div>
        <div className="font-mono text-xs break-all text-muted-foreground" data-testid="ice-metered-url">
          {data.metered_url || '(ikke konfigureret)'}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <span className="text-xs text-muted-foreground">Sidste HTTP-status:</span>
          {status === 200 ? (
            <Badge className="bg-volt/10 text-volt border-volt/30 hover:bg-volt/10">200 OK</Badge>
          ) : status ? (
            <Badge variant="outline" className="border-destructive/40 text-destructive">{status}</Badge>
          ) : (
            <Badge variant="outline" className="border-border text-muted-foreground">Ikke kaldt</Badge>
          )}
        </div>
        {data.last_error && (
          <div className="mt-3 rounded border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive break-all">
            {data.last_error}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-[#18181B] p-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Aktive ICE-servers</div>
        <div className="space-y-3" data-testid="ice-active-list">
          {(data.active_ice_servers || []).map((s, i) => {
            const urls = Array.isArray(s.urls) ? s.urls : [s.urls];
            const isTurn = urls.some((u) => u && u.startsWith('turn'));
            return (
              <div key={i} className="rounded-md bg-[#09090B] border border-border p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={isTurn ? 'bg-orange-500/15 text-orange-400 border-orange-500/30' : 'bg-volt/10 text-volt border-volt/30 hover:bg-volt/10'}>
                    {isTurn ? 'TURN' : 'STUN'}
                  </Badge>
                  {s.username && <span className="text-xs text-muted-foreground">user: {s.username}</span>}
                </div>
                <ul className="space-y-1 font-mono text-xs">
                  {urls.map((u, j) => <li key={j} className="text-muted-foreground">{u}</li>)}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-[#18181B] p-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Fallback-config (env)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
          <div>
            <div className="text-muted-foreground uppercase text-[10px] mb-1">STUN_URLS</div>
            <ul className="space-y-0.5">
              {(data.fallback_stun_urls || []).filter(Boolean).map((u, i) => <li key={i}>{u}</li>)}
            </ul>
          </div>
          <div>
            <div className="text-muted-foreground uppercase text-[10px] mb-1">TURN_URLS</div>
            <ul className="space-y-0.5">
              {(data.fallback_turn_urls || []).filter(Boolean).map((u, i) => <li key={i}>{u}</li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- BROADCAST EMAIL ---------------- */
function BroadcastTab() {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [recipientCount, setRecipientCount] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/users');
        setRecipientCount(data.length);
      } catch { /* non-blocking */ }
    })();
  }, []);

  const submit = async () => {
    setConfirmOpen(false);
    setSending(true);
    try {
      const { data } = await api.post('/admin/broadcast-email', {
        subject: subject.trim(),
        content_html: content,
      });
      toast.success(`Email sendt: ${data.sent}/${data.total} leveret${data.failed ? ` (${data.failed} fejlede)` : ''}`);
      setSubject('');
      setContent('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const openConfirm = () => {
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!content.trim()) { toast.error('Content is required'); return; }
    setConfirmOpen(true);
  };

  return (
    <div className="max-w-3xl space-y-6 rounded-lg border border-border bg-[#18181B] p-6 md:p-8">
      <div>
        <h3 className="font-display text-2xl font-bold mb-1 flex items-center gap-2"><Mail className="w-5 h-5 text-volt" /> Broadcast email</h3>
        <p className="text-sm text-muted-foreground">Send an email to all {recipientCount ?? '...'} registered users. HTML is supported in the content field.</p>
      </div>

      <div>
        <Label>Subject</Label>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="e.g. New features on samtale24-7.dk"
          className="bg-[#09090B] border-border mt-1.5"
          data-testid="broadcast-subject-input"
        />
      </div>

      <div>
        <Label>Content (HTML allowed)</Label>
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={12}
          placeholder="<p>Hi everyone,</p><p>...</p>"
          className="bg-[#09090B] border-border mt-1.5 font-mono text-sm"
          data-testid="broadcast-content-input"
        />
      </div>

      <div className="flex items-center justify-end gap-3">
        <Button
          onClick={openConfirm}
          disabled={sending}
          className="bg-volt text-black hover:bg-volt font-semibold"
          data-testid="broadcast-send-btn"
        >
          <Send className="w-4 h-4 mr-2" />
          {sending ? 'Sending…' : `Send to ${recipientCount ?? '...'} users`}
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader>
            <DialogTitle>Confirm broadcast</DialogTitle>
            <DialogDescription>You are about to send an email to <strong>{recipientCount}</strong> users. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <div><span className="text-muted-foreground">Subject:</span> <strong>{subject}</strong></div>
            <div className="text-muted-foreground">Content: <span className="font-mono">{content.length} characters</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={submit} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="broadcast-confirm-btn">Send now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ---------------- USERSPRIVATEGROUPS ---------------- */
function UsersPrivateGroupsTab() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { id: '', user_id: '', group_id: '', nickname: '', invitation_date: '', acceptance_date: '', ip_address: '', device_name: '' };
  const [newRow, setNewRow] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: us }, { data: gs }] = await Promise.all([
        api.get('/admin/users-private-groups'),
        api.get('/admin/users'),
        api.get('/admin/groups'),
      ]);
      setRows(list); setUsers(us); setGroups(gs);
    } catch (err) { toast.error(err.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const userName = (id) => { const u = users.find((x) => x.id === id); return u ? (u.nickname || u.email) : id?.slice(0, 8); };
  const groupName = (id) => { const g = groups.find((x) => x.id === id); return g ? g.name : (id ? id.slice(0, 8) : '—'); };

  const create = async () => {
    if (!newRow.user_id) { toast.error('User is required'); return; }
    if (!newRow.group_id) { toast.error('Private group is required'); return; }
    try {
      // If admin left ID blank, backend generates a UUID. Otherwise send the custom ID.
      // Nickname is derived from the user record if not provided (kept as legacy field for backward compat).
      const payload = { ...newRow };
      if (!payload.id || !payload.id.trim()) delete payload.id;
      if (!payload.nickname) {
        const u = users.find((x) => x.id === payload.user_id);
        payload.nickname = u?.nickname || u?.email || '';
      }
      await api.post('/admin/users-private-groups', payload);
      toast.success('Created'); setCreating(false); setNewRow(empty); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Create failed'); }
  };
  const save = async () => {
    try {
      const { id, ...rest } = editing;
      // Support renaming the ID: if editing.id differs from the original row's id, we DELETE-then-INSERT.
      const original = rows.find((r) => r.id === editing._originalId || r.id === id);
      const newId = (id || '').trim();
      const originalId = editing._originalId || (original ? original.id : id);
      if (newId && originalId && newId !== originalId) {
        // Rename: create new record with the new ID, then delete the old one
        await api.post('/admin/users-private-groups', { id: newId, ...rest, user_id: original?.user_id, group_id: original?.group_id });
        await api.delete(`/admin/users-private-groups/${originalId}`);
      } else {
        await api.put(`/admin/users-private-groups/${originalId}`, rest);
      }
      toast.success('Updated'); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete record?')) return;
    try { await api.delete(`/admin/users-private-groups/${id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="rounded-lg border border-border bg-[#18181B] p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl font-bold">USERSPRIVATEGROUPS</h3>
        <Button size="sm" onClick={() => setCreating(true)} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="uspg-create-btn"><Plus className="w-4 h-4 mr-1" /> New</Button>
      </div>
      <Table>
        <TableHeader><TableRow className="border-border hover:bg-transparent">
          <TableHead>ID</TableHead><TableHead>User</TableHead><TableHead>Private Group</TableHead>
          <TableHead>Invitation</TableHead><TableHead>Acceptance</TableHead><TableHead>IP</TableHead><TableHead>Device</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow></TableHeader>
        <TableBody data-testid="admin-uspg-table">
          {loading ? <TableRow className="border-border"><TableCell colSpan={8} className="text-center text-muted-foreground">Loading…</TableCell></TableRow>
          : rows.length === 0 ? <TableRow className="border-border"><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No records.</TableCell></TableRow>
          : rows.map((r) => (
            <TableRow key={r.id} className="border-border">
              <TableCell className="font-mono text-xs max-w-[140px] truncate" title={r.id}>{r.id}</TableCell>
              <TableCell className="font-medium">{userName(r.user_id) || <span className="text-red-400 italic">(missing user)</span>}</TableCell>
              <TableCell>{groupName(r.group_id) || <span className="text-red-400 italic">(no group)</span>}</TableCell>
              <TableCell className="text-xs">{r.invitation_date ? r.invitation_date.slice(0, 16) : '—'}</TableCell>
              <TableCell className="text-xs">{r.acceptance_date ? r.acceptance_date.slice(0, 16) : '—'}</TableCell>
              <TableCell className="text-xs font-mono">{r.ip_address || '—'}</TableCell>
              <TableCell className="text-xs max-w-[200px] truncate" title={r.device_name}>{r.device_name || '—'}</TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r, _originalId: r.id })}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader><DialogTitle>New USERSPRIVATEGROUPS record</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>ID (optional — auto-generated if left blank)</Label><Input value={newRow.id} onChange={(e) => setNewRow({ ...newRow, id: e.target.value })} className="bg-[#09090B] border-border mt-1.5 font-mono text-xs" placeholder="Leave blank to auto-generate" data-testid="new-uspg-id-input" /></div>
            <div><Label>User <span className="text-red-500">*</span></Label>
              <Select value={newRow.user_id} onValueChange={(v) => setNewRow({ ...newRow, user_id: v })}>
                <SelectTrigger className="bg-[#09090B] border-border mt-1.5" data-testid="new-uspg-user-select"><SelectValue placeholder="Pick a user" /></SelectTrigger>
                <SelectContent className="bg-[#18181B] border-border">{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.nickname || u.email} ({u.email})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Group ID <span className="text-red-500">*</span></Label>
              <Input
                value={newRow.group_id}
                onChange={(e) => setNewRow({ ...newRow, group_id: e.target.value })}
                className="bg-[#09090B] border-border mt-1.5 font-mono text-xs"
                placeholder="Paste the GROUPS.id (e.g. 749dc21e-cac5-4d83-bb30-a5924a4e02c7)"
                list="uspg-groups-datalist"
                data-testid="new-uspg-group-id-input"
              />
              <datalist id="uspg-groups-datalist">
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}{g.private ? ' (private)' : ''}</option>)}
              </datalist>
              <p className="text-xs text-muted-foreground mt-1">Type or paste any GROUPS.id. Autocomplete suggests existing groups.</p>
            </div>
            <div><Label>Invitation date</Label><Input type="datetime-local" value={newRow.invitation_date} onChange={(e) => setNewRow({ ...newRow, invitation_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            <div><Label>Acceptance date</Label><Input type="datetime-local" value={newRow.acceptance_date} onChange={(e) => setNewRow({ ...newRow, acceptance_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            <div><Label>IP address</Label><Input value={newRow.ip_address} onChange={(e) => setNewRow({ ...newRow, ip_address: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            <div><Label>Device name</Label><Input value={newRow.device_name} onChange={(e) => setNewRow({ ...newRow, device_name: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)} className="border-border">Cancel</Button>
            <Button onClick={create} className="bg-volt text-black hover:bg-volt font-semibold">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader><DialogTitle>Edit USERSPRIVATEGROUPS record</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>ID (change to rename this record)</Label><Input value={editing.id || ''} onChange={(e) => setEditing({ ...editing, id: e.target.value })} className="bg-[#09090B] border-border mt-1.5 font-mono text-xs" data-testid="edit-uspg-id-input" /></div>
              <div><Label>User</Label>
                <Select value={editing.user_id || ''} onValueChange={(v) => setEditing({ ...editing, user_id: v })}>
                  <SelectTrigger className="bg-[#09090B] border-border mt-1.5" data-testid="edit-uspg-user-select"><SelectValue placeholder="Pick a user" /></SelectTrigger>
                  <SelectContent className="bg-[#18181B] border-border">{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.nickname || u.email} ({u.email})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Group ID</Label>
                <Input
                  value={editing.group_id || ''}
                  onChange={(e) => setEditing({ ...editing, group_id: e.target.value })}
                  className="bg-[#09090B] border-border mt-1.5 font-mono text-xs"
                  placeholder="Paste the GROUPS.id"
                  list="uspg-groups-datalist-edit"
                  data-testid="edit-uspg-group-id-input"
                />
                <datalist id="uspg-groups-datalist-edit">
                  {groups.map((g) => <option key={g.id} value={g.id}>{g.name}{g.private ? ' (private)' : ''}</option>)}
                </datalist>
              </div>
              <div><Label>Acceptance date</Label><Input type="datetime-local" value={(editing.acceptance_date || '').slice(0, 16)} onChange={(e) => setEditing({ ...editing, acceptance_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>IP address</Label><Input value={editing.ip_address || ''} onChange={(e) => setEditing({ ...editing, ip_address: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>Device name</Label><Input value={editing.device_name || ''} onChange={(e) => setEditing({ ...editing, device_name: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-border">Cancel</Button>
            <Button onClick={save} className="bg-volt text-black hover:bg-volt font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ---------------- FREEUSERSINFO ---------------- */
function FreeUsersInfoTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { email: '', device_name: '', ip_address: '', fingerprint: '', remain_free_minutes: 0, creation_date: '', acceptance_date: '' };
  const [newRow, setNewRow] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/free-users-info');
      setRows(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newRow.email) { toast.error('Email is required'); return; }
    try {
      await api.post('/admin/free-users-info', {
        ...newRow,
        remain_free_minutes: parseInt(newRow.remain_free_minutes) || 0,
      });
      toast.success('Created'); setCreating(false); setNewRow(empty); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Create failed'); }
  };
  const save = async () => {
    try {
      const { id, ...payload } = editing;
      payload.remain_free_minutes = parseInt(payload.remain_free_minutes) || 0;
      await api.put(`/admin/free-users-info/${id}`, payload);
      toast.success('Updated'); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete record?')) return;
    try { await api.delete(`/admin/free-users-info/${id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="rounded-lg border border-border bg-[#18181B] p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl font-bold">Free Users Info</h3>
        <Button size="sm" onClick={() => setCreating(true)} className="bg-volt text-black hover:bg-volt font-semibold" data-testid="fui-create-btn"><Plus className="w-4 h-4 mr-1" /> New</Button>
      </div>
      <Table>
        <TableHeader><TableRow className="border-border hover:bg-transparent">
          <TableHead>Email</TableHead><TableHead>Device</TableHead><TableHead>IP</TableHead>
          <TableHead>Fingerprint</TableHead><TableHead>Remain min.</TableHead>
          <TableHead>Created</TableHead><TableHead>Accepted</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow></TableHeader>
        <TableBody data-testid="admin-fui-table">
          {loading ? <TableRow className="border-border"><TableCell colSpan={8} className="text-center text-muted-foreground">Loading…</TableCell></TableRow>
          : rows.length === 0 ? <TableRow className="border-border"><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No records.</TableCell></TableRow>
          : rows.map((r) => (
            <TableRow key={r.id} className="border-border">
              <TableCell className="font-medium">{r.email}</TableCell>
              <TableCell className="text-xs max-w-[180px] truncate" title={r.device_name}>{r.device_name || '—'}</TableCell>
              <TableCell className="text-xs font-mono">{r.ip_address || '—'}</TableCell>
              <TableCell className="text-xs font-mono max-w-[120px] truncate" title={r.fingerprint}>{r.fingerprint || '—'}</TableCell>
              <TableCell>{r.remain_free_minutes ?? 0}</TableCell>
              <TableCell className="text-xs">{r.creation_date ? r.creation_date.slice(0, 16) : '—'}</TableCell>
              <TableCell className="text-xs">{r.acceptance_date ? r.acceptance_date.slice(0, 16) : '—'}</TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r })}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader><DialogTitle>New FreeUsersInfo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Email</Label><Input type="email" value={newRow.email} onChange={(e) => setNewRow({ ...newRow, email: e.target.value })} className="bg-[#09090B] border-border mt-1.5" data-testid="new-fui-email-input" /></div>
            <div><Label>Device name</Label><Input value={newRow.device_name} onChange={(e) => setNewRow({ ...newRow, device_name: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            <div><Label>IP address</Label><Input value={newRow.ip_address} onChange={(e) => setNewRow({ ...newRow, ip_address: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            <div><Label>Fingerprint</Label><Input value={newRow.fingerprint} onChange={(e) => setNewRow({ ...newRow, fingerprint: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            <div><Label>Remaining free minutes</Label><Input type="number" min="0" value={newRow.remain_free_minutes} onChange={(e) => setNewRow({ ...newRow, remain_free_minutes: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            <div><Label>Acceptance date</Label><Input type="datetime-local" value={newRow.acceptance_date} onChange={(e) => setNewRow({ ...newRow, acceptance_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)} className="border-border">Cancel</Button>
            <Button onClick={create} className="bg-volt text-black hover:bg-volt font-semibold">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader><DialogTitle>Edit record</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Email</Label><Input type="email" value={editing.email || ''} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>Device name</Label><Input value={editing.device_name || ''} onChange={(e) => setEditing({ ...editing, device_name: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>IP address</Label><Input value={editing.ip_address || ''} onChange={(e) => setEditing({ ...editing, ip_address: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>Fingerprint</Label><Input value={editing.fingerprint || ''} onChange={(e) => setEditing({ ...editing, fingerprint: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>Remaining free minutes</Label><Input type="number" min="0" value={editing.remain_free_minutes ?? 0} onChange={(e) => setEditing({ ...editing, remain_free_minutes: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
              <div><Label>Acceptance date</Label><Input type="datetime-local" value={(editing.acceptance_date || '').slice(0, 16)} onChange={(e) => setEditing({ ...editing, acceptance_date: e.target.value })} className="bg-[#09090B] border-border mt-1.5" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-border">Cancel</Button>
            <Button onClick={save} className="bg-volt text-black hover:bg-volt font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

